import type { Event } from '@/lib/events';
import { effectiveEventEnd, hasEventEnded } from '@sixam/shared/event-time';

type HomeFeedEvent = Pick<
  Event,
  'created_at' | 'ends_at' | 'id' | 'registration_closes_at' | 'starts_at'
>;

function eventUrgencyTime(event: HomeFeedEvent, now: Date): number {
  const nowTime = now.getTime();
  const startsAt = new Date(event.starts_at).getTime();
  const endsAt = effectiveEventEnd(event).getTime();

  if (startsAt <= nowTime && nowTime < endsAt) {
    return Number.NEGATIVE_INFINITY;
  }

  const registrationClosesAt = event.registration_closes_at
    ? new Date(event.registration_closes_at).getTime()
    : Number.POSITIVE_INFINITY;

  return registrationClosesAt > nowTime ? Math.min(startsAt, registrationClosesAt) : startsAt;
}

/**
 * Keeps live events first, then orders upcoming events by the next thing a member
 * can miss: either the event start or its registration deadline.
 */
export function rankHomeFeedEvents<T extends HomeFeedEvent>(events: readonly T[], now: Date): T[] {
  return events
    .filter((event) => !hasEventEnded(event, now))
    .sort((left, right) => {
      const urgencyDifference = eventUrgencyTime(left, now) - eventUrgencyTime(right, now);
      if (urgencyDifference !== 0) return urgencyDifference;

      const startDifference =
        new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime();
      if (startDifference !== 0) return startDifference;

      const creationDifference =
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
      if (creationDifference !== 0) return creationDifference;

      return left.id.localeCompare(right.id);
    });
}
